import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, Lightbulb, Plus, Trash2, Edit2, Save, X, 
  ChevronDown, ChevronRight, Sparkles, Clock, CheckCircle, 
  AlertCircle, Filter
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: 'idea', label: 'Idé', color: 'bg-purple-500/20 text-purple-400', icon: Lightbulb },
  { value: 'planned', label: 'Planlagt', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  { value: 'in_progress', label: 'I gang', color: 'bg-yellow-500/20 text-yellow-400', icon: AlertCircle },
  { value: 'done', label: 'Færdig', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
];

const PRIORITY_OPTIONS = [
  { value: 'P1', label: 'P1 - Høj', color: 'bg-red-500/20 text-red-400' },
  { value: 'P2', label: 'P2 - Medium', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'P3', label: 'P3 - Lav', color: 'bg-green-500/20 text-green-400' },
];

const CATEGORY_OPTIONS = [
  { value: 'module', label: 'Nyt modul' },
  { value: 'feature', label: 'Ny funktion' },
  { value: 'integration', label: 'Integration' },
  { value: 'improvement', label: 'Forbedring' },
  { value: 'other', label: 'Andet' },
];

export default function Idebank({ user, onLogout, onBack }) {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [expandedIdeas, setExpandedIdeas] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  
  const [newIdea, setNewIdea] = useState({
    title: '',
    description: '',
    concept: '',
    status: 'idea',
    priority: 'P2',
    category: 'feature',
    notes: ''
  });

  const token = localStorage.getItem("token");
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      loadIdeas();
    }
  }, [isAdmin]);

  const loadIdeas = async () => {
    try {
      const res = await axios.get(`${API}/idebank`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIdeas(res.data);
    } catch (error) {
      console.error("Kunne ikke hente idéer", error);
      toast.error("Kunne ikke hente idéer");
    } finally {
      setLoading(false);
    }
  };

  const saveIdea = async () => {
    if (!newIdea.title.trim()) {
      toast.error("Indtast en titel");
      return;
    }

    try {
      if (editingIdea) {
        await axios.put(`${API}/idebank/${editingIdea.id}`, newIdea, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Idé opdateret!");
      } else {
        await axios.post(`${API}/idebank`, newIdea, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Idé tilføjet!");
      }
      
      setShowAddForm(false);
      setEditingIdea(null);
      setNewIdea({ title: '', description: '', concept: '', status: 'idea', priority: 'P2', category: 'feature', notes: '' });
      loadIdeas();
    } catch (error) {
      toast.error("Fejl ved gemning af idé");
    }
  };

  const deleteIdea = (idea) => {
    setConfirmDialog({
      open: true,
      title: "Slet idé?",
      message: `Er du sikker på du vil slette "${idea.title}"?`,
      onConfirm: async () => {
        setConfirmDialog({ open: false });
        try {
          await axios.delete(`${API}/idebank/${idea.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Idé slettet");
          loadIdeas();
        } catch (error) {
          toast.error("Fejl ved sletning");
        }
      }
    });
  };

  const startEdit = (idea) => {
    setEditingIdea(idea);
    setNewIdea({
      title: idea.title,
      description: idea.description || '',
      concept: idea.concept || '',
      status: idea.status || 'idea',
      priority: idea.priority || 'P2',
      category: idea.category || 'feature',
      notes: idea.notes || ''
    });
    setShowAddForm(true);
  };

  const toggleExpand = (ideaId) => {
    const newExpanded = new Set(expandedIdeas);
    if (newExpanded.has(ideaId)) {
      newExpanded.delete(ideaId);
    } else {
      newExpanded.add(ideaId);
    }
    setExpandedIdeas(newExpanded);
  };

  const updateIdeaStatus = async (idea, newStatus) => {
    try {
      await axios.put(`${API}/idebank/${idea.id}`, { ...idea, status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadIdeas();
    } catch (error) {
      toast.error("Fejl ved opdatering");
    }
  };

  // Filtrering
  const filteredIdeas = ideas.filter(idea => {
    if (filterStatus !== 'all' && idea.status !== filterStatus) return false;
    if (filterPriority !== 'all' && idea.priority !== filterPriority) return false;
    return true;
  });

  // Gruppér efter status
  const groupedIdeas = {
    idea: filteredIdeas.filter(i => i.status === 'idea'),
    planned: filteredIdeas.filter(i => i.status === 'planned'),
    in_progress: filteredIdeas.filter(i => i.status === 'in_progress'),
    done: filteredIdeas.filter(i => i.status === 'done'),
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Lightbulb className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Ingen adgang</h2>
          <p className="text-muted-foreground">Kun administratorer har adgang til Idébanken</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="idebank-page">
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false })}
      />

      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack || (() => navigate(-1))}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                <Sparkles className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold font-heading uppercase tracking-tight">
                  Idébank
                </h1>
                <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
                  Fremtidige funktioner og idéer
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground font-mono uppercase">{user?.role}</p>
            </div>
            <Button onClick={onLogout} variant="ghost" size="sm">
              Log ud
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1.5 bg-zinc-800 border border-border rounded text-sm"
              >
                <option value="all">Alle statusser</option>
                {STATUS_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-1.5 bg-zinc-800 border border-border rounded text-sm"
            >
              <option value="all">Alle prioriteter</option>
              {PRIORITY_OPTIONS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          
          <Button onClick={() => { setShowAddForm(true); setEditingIdea(null); setNewIdea({ title: '', description: '', concept: '', status: 'idea', priority: 'P2', category: 'feature', notes: '' }); }}>
            <Plus className="h-4 w-4 mr-2" />
            Tilføj idé
          </Button>
        </div>

        {/* Tilføj/Rediger form */}
        {showAddForm && (
          <div className="p-6 bg-card border border-border rounded-lg mb-6">
            <h3 className="font-bold text-lg mb-4">
              {editingIdea ? 'Rediger idé' : 'Tilføj ny idé'}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium block mb-1">Titel *</label>
                  <Input
                    placeholder="F.eks. Alarm-modul"
                    value={newIdea.title}
                    onChange={(e) => setNewIdea({...newIdea, title: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Kategori</label>
                  <select
                    value={newIdea.category}
                    onChange={(e) => setNewIdea({...newIdea, category: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-border rounded text-sm"
                  >
                    {CATEGORY_OPTIONS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium block mb-1">Kort beskrivelse</label>
                <Input
                  placeholder="Hvad handler idéen om?"
                  value={newIdea.description}
                  onChange={(e) => setNewIdea({...newIdea, description: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium block mb-1">Koncept / Detaljer</label>
                <textarea
                  placeholder="Uddybende beskrivelse, tekniske detaljer, eksempler..."
                  value={newIdea.concept}
                  onChange={(e) => setNewIdea({...newIdea, concept: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-border rounded text-sm min-h-[150px]"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Status</label>
                  <select
                    value={newIdea.status}
                    onChange={(e) => setNewIdea({...newIdea, status: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-border rounded text-sm"
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Prioritet</label>
                  <select
                    value={newIdea.priority}
                    onChange={(e) => setNewIdea({...newIdea, priority: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-border rounded text-sm"
                  >
                    {PRIORITY_OPTIONS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium block mb-1">Noter</label>
                <textarea
                  placeholder="Egne noter, spørgsmål, afklaringer..."
                  value={newIdea.notes}
                  onChange={(e) => setNewIdea({...newIdea, notes: e.target.value})}
                  className="w-full px-3 py-2 bg-zinc-800 border border-border rounded text-sm min-h-[80px]"
                />
              </div>
              
              <div className="flex gap-2">
                <Button onClick={saveIdea}>
                  <Save className="h-4 w-4 mr-2" />
                  {editingIdea ? 'Gem ændringer' : 'Tilføj idé'}
                </Button>
                <Button variant="outline" onClick={() => { setShowAddForm(false); setEditingIdea(null); }}>
                  <X className="h-4 w-4 mr-2" />
                  Annuller
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Idé-liste */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            Henter idéer...
          </div>
        ) : filteredIdeas.length === 0 ? (
          <div className="text-center py-16 bg-card/30 rounded-lg border border-dashed border-border">
            <Lightbulb className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Ingen idéer endnu</h3>
            <p className="text-muted-foreground mb-4">
              Tilføj din første idé for at komme i gang
            </p>
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Tilføj idé
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {STATUS_OPTIONS.map(statusOption => {
              const statusIdeas = groupedIdeas[statusOption.value] || [];
              if (filterStatus !== 'all' && filterStatus !== statusOption.value) return null;
              if (statusIdeas.length === 0 && filterStatus === 'all') return null;
              
              const StatusIcon = statusOption.icon;
              
              return (
                <div key={statusOption.value}>
                  <div className="flex items-center gap-2 mb-3">
                    <StatusIcon className="h-5 w-5" />
                    <h2 className="text-lg font-bold">{statusOption.label}</h2>
                    <span className="text-sm text-muted-foreground">({statusIdeas.length})</span>
                  </div>
                  
                  {statusIdeas.length === 0 ? (
                    <div className="p-4 bg-card/30 rounded-lg border border-dashed border-border text-center text-muted-foreground">
                      Ingen idéer med denne status
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {statusIdeas.map(idea => {
                        const isExpanded = expandedIdeas.has(idea.id);
                        const priorityOption = PRIORITY_OPTIONS.find(p => p.value === idea.priority);
                        const categoryOption = CATEGORY_OPTIONS.find(c => c.value === idea.category);
                        
                        return (
                          <div 
                            key={idea.id}
                            className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary/50 transition-colors"
                          >
                            {/* Header */}
                            <div 
                              className="p-4 flex items-center justify-between cursor-pointer"
                              onClick={() => toggleExpand(idea.id)}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-bold">{idea.title}</h3>
                                    <span className={`text-xs px-2 py-0.5 rounded ${priorityOption?.color || ''}`}>
                                      {priorityOption?.label || idea.priority}
                                    </span>
                                    <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-300">
                                      {categoryOption?.label || idea.category}
                                    </span>
                                  </div>
                                  {idea.description && (
                                    <p className="text-sm text-muted-foreground mt-1">{idea.description}</p>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <select
                                  value={idea.status}
                                  onChange={(e) => updateIdeaStatus(idea, e.target.value)}
                                  className={`text-xs px-2 py-1 rounded border-0 cursor-pointer ${statusOption.color}`}
                                >
                                  {STATUS_OPTIONS.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                  ))}
                                </select>
                                <Button variant="ghost" size="sm" onClick={() => startEdit(idea)}>
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteIdea(idea)} className="text-destructive hover:bg-destructive/10">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Expanded content */}
                            {isExpanded && (idea.concept || idea.notes) && (
                              <div className="px-4 pb-4 pt-0 border-t border-border">
                                {idea.concept && (
                                  <div className="mt-4">
                                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Koncept / Detaljer</h4>
                                    <div className="p-3 bg-muted/30 rounded text-sm whitespace-pre-wrap">
                                      {idea.concept}
                                    </div>
                                  </div>
                                )}
                                {idea.notes && (
                                  <div className="mt-4">
                                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Noter</h4>
                                    <div className="p-3 bg-yellow-500/10 rounded text-sm whitespace-pre-wrap border border-yellow-500/20">
                                      {idea.notes}
                                    </div>
                                  </div>
                                )}
                                <div className="mt-4 text-xs text-muted-foreground">
                                  Oprettet: {new Date(idea.created_at).toLocaleString('da-DK')}
                                  {idea.updated_at && ` • Opdateret: ${new Date(idea.updated_at).toLocaleString('da-DK')}`}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
